import { inject, Injectable } from '@angular/core';
import { RoleTypes } from '../../constants/role-types.constant';
import { AppStorage } from '../../enums/app-storage';
import { Permission } from '../../models/permission.model';
import { AppPermissions } from '../../../../core/common/constants/app-permissions';
import { CookieService } from '../cookie/cookie.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  readonly cookieService = inject(CookieService);
  /**
   * Checks if the user has the required permissions for a given feature.
   *
   * @param featurePermissions - An array of required permissions for the feature.
   * @param userPermissions - An array of the user's permissions.
   * @returns A boolean indicating whether the user has the required permissions.
   *
   * @remarks
   * - If the user has the role 'super admin', they automatically have permission.
   * - If no feature permissions are provided, the user has permission.
   * - If no user permissions are provided, the user does not have any permission.
   */
  checkPermission(
    featurePermissions: readonly string[] | undefined,
    userPermissions: Permission[] | undefined
  ): boolean {
    let hasPermission = false;
    const role = this.cookieService.get(AppStorage.role);
    const isRoleAdmin = role
      ? JSON.parse(role).includes(RoleTypes.SUPER_ADMIN)
      : false;
    const hasOwnPermission =
      userPermissions?.some(
        permission =>
          permission.action.toUpperCase() === AppPermissions.owner.toUpperCase()
      ) ?? false;

    if (
      isRoleAdmin ||
      userPermissions === undefined ||
      !featurePermissions?.length ||
      hasOwnPermission
    ) {
      hasPermission = true;
    } else if (featurePermissions?.length && userPermissions?.length) {
      for (const checkPermission of featurePermissions) {
        hasPermission = userPermissions.some(
          x => x.action.toUpperCase() === checkPermission.toUpperCase()
        );

        if (hasPermission) {
          break;
        }
      }
    }

    return hasPermission;
  }
}
